import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";

const LANES = [["leader", "Leader"], ["on_pace", "On pace"], ["midfield", "Midfield"], ["back", "Back"]];

const Chip = ({ r }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#0B101D] border border-white/10 text-xs whitespace-nowrap" data-testid={`speedmap-runner-${r.number}`}>
    <span className="font-mono font-bold text-blue-300">{r.number}</span>{r.horse}<span className="text-slate-500">({r.barrier ?? "-"})</span>
  </span>
);

export const SpeedMap = ({ meetingId, race, isAdmin, onSaved }) => {
  const api = useApi();
  const runners = race.runners.filter((r) => !r.scratched).sort((a, b) => (a.barrier ?? 99) - (b.barrier ?? 99));
  const KROK = { leader: "leader", pace: "on_pace", "off pace": "on_pace", midfield: "midfield", "off midfield": "midfield", backmarker: "back" };
  const provider = Object.fromEntries(race.runners.filter((r) => r.krok_speedmap && KROK[r.krok_speedmap.toLowerCase()]).map((r) => [r.number, KROK[r.krok_speedmap.toLowerCase()]]));
  const own = race.speed_map?.styles || {};
  const saved = { ...provider, ...own };
  const fromProvider = Object.keys(provider).length > 0;
  const [edit, setEdit] = useState(false);
  const [styles, setStyles] = useState(() => saved);
  useEffect(() => { setStyles({ ...provider, ...(race.speed_map?.styles || {}) }); setEdit(false); }, [race.number, race.speed_map]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (!runners.length) return null;
  const tagged = Object.keys(saved).length > 0;
  const n = runners.length;
  const group = (r) => (r.barrier == null ? "Unknown" : r.barrier <= Math.ceil(n / 3) ? "Inside" : r.barrier <= Math.ceil((2 * n) / 3) ? "Middle" : "Wide");

  const save = async () => {
    try { await api("put", `/admin/meetings/${meetingId}/races/${race.number}/speed-map`, { styles: Object.fromEntries(Object.entries(styles).filter(([, v]) => v)) }); toast.success("Speed map saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5" data-testid="speed-map">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className="font-display font-bold flex items-center gap-2 mr-auto"><Gauge className="w-4 h-4 text-blue-400" />Speed map <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/40 rounded px-1.5">Estimate</span></h3>
        {isAdmin && <button data-testid="speedmap-edit-button" onClick={() => setEdit(!edit)} className="text-xs text-blue-400 hover:underline">{edit ? "Close" : "Tag run styles"}</button>}
      </div>
      <p className="text-xs text-slate-500 mb-3" data-testid="speedmap-note">
        {fromProvider ? <>Settling positions from <a href="https://krokodds.com.au" target="_blank" rel="noopener noreferrer" className="underline">Krok Odds</a>{Object.keys(own).length ? ", adjusted by The Weekend Edge" : ""}. An estimate of how the race may be run, not a guarantee.</> : tagged ? "Run styles are The Weekend Edge's estimates; untagged runners are shown by barrier. Not official data." : "No run styles yet for this race, so runners are grouped by barrier only. This is an estimate, not official data."}
      </p>
      {edit ? (
        <div className="space-y-1.5" data-testid="speedmap-editor">
          {runners.map((r) => (
            <label key={r.number} className="flex items-center gap-2 text-sm">
              <span className="w-48 truncate"><span className="font-mono text-blue-300">{r.number}</span> {r.horse} <span className="text-slate-500">({r.barrier ?? "-"})</span></span>
              <select data-testid={`speedmap-style-${r.number}`} value={styles[r.number] || ""} onChange={(e) => setStyles({ ...styles, [r.number]: e.target.value })}
                className="bg-[#0B101D] border border-white/10 rounded px-2 py-1 text-xs">
                <option value="">Not sure</option>
                {LANES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
          ))}
          <button data-testid="speedmap-save-button" onClick={save} className="mt-2 px-4 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-xs font-semibold">Save speed map</button>
        </div>
      ) : (
        <div className="space-y-2">
          {(tagged ? [...LANES, ["", "Untagged"]] : [["Inside"], ["Middle"], ["Wide"], ["Unknown"]]).map(([k, l]) => {
            const rows = tagged ? runners.filter((r) => (saved[r.number] || "") === k) : runners.filter((r) => group(r) === k);
            if (!rows.length) return null;
            return (
              <div key={l || k} className="flex gap-3 items-start" data-testid={`speedmap-lane-${(l || k).toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="w-20 shrink-0 text-[11px] uppercase tracking-widest text-slate-500 pt-1.5">{tagged ? l : `${k} gates`}</span>
                <div className="flex flex-wrap gap-1.5">{rows.map((r) => <Chip key={r.number} r={r} />)}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
